import React, {useEffect} from 'react'
import {Form, useInputValue} from '@modifi/plugin-forms'
import {
  CheckboxGroupPureContainer,
  FormInputWrapper as InputWrapper,
} from '@modifi/plugin-onboarding'
import {Col, H6, InfoAlert, InlineLoader, Row, Small} from '@modifi/ui'
import {Trans, useTranslation} from '@modifi/plugin-i18n'

import {useDataPrefillLoader} from '../hooks'
import {mapPersonsToOptions} from './utils'
import {StyledLink, CheckBoxWrapper} from './styles'
import {ConnectedFormFooter} from '../../../plugins/onboarding/components/form-cta-footer'
import {FormContentWrapper} from '../styles'
import {LEGAL_REPRESENTATIVES_ENTITY, ULTIMATE_BENEFICIAL_OWNERS_ENTITY} from './constants'
import {
  SECTION_LEGAL_REPRESENTATIVES,
  SECTION_ULTIMATE_BENEFICIAL_OWNERS,
} from '../../../constants/sections'
import {EntityFormData} from '../../../types/common'

type ConnectedFormFooterWrapperProps = {
  hasSourceData: boolean
  hasError: boolean
}

const ConnectedFormFooterWrapper: React.VFC<ConnectedFormFooterWrapperProps> = ({
  hasSourceData,
  ...restProps
}) => {
  const {value: legalRepresentatives} = useInputValue({
    name: LEGAL_REPRESENTATIVES_ENTITY,
  })

  const {value: beneficialOwners} = useInputValue({
    name: ULTIMATE_BENEFICIAL_OWNERS_ENTITY,
  })

  const lrs = legalRepresentatives as any[]
  const ubos = beneficialOwners as any[]

  return (
    <ConnectedFormFooter
      {...restProps}
      disableSubmit={
        hasSourceData &&
        // value retured for legalreps & ubos above from CheckboxGroupPureContainer is like ['', 'actual value']
        // i.e. an extra unnecessary string is also added, so to quickly fix issue for now we have
        // added a check for this condition rn, instead of fixing it in CheckboxGroupPureContainer
        !!(lrs?.length === 0 || (lrs.length === 1 && lrs[0] === '')) &&
        !!(ubos?.length === 0 || (ubos.length === 1 && ubos[0] === ''))
      }
    />
  )
}

/** A person offered by the selected pre-fill data source. */
type DataSourcePerson = {
  firstName?: string | null
  lastName?: string | null
  providerPersonId: string
}

export type DataSourceWizardProps = {
  sourcesAreLoading: boolean
  initialFormData: EntityFormData
  legalRepresentativesOfSelectedSource: DataSourcePerson[]
  ultimateBeneficialOwnersOfSelectedSource: DataSourcePerson[]
  providerDetails?: {
    name?: string
    website?: string
  }
  selectedDataSourceDetailsAreLoading: boolean
  submit: (changes: Record<string, unknown>) => void
  submitPending: boolean
  hasError?: boolean
  skip: (personRoleType: string) => void
  countryCode?: string | null
  initialize: () => void
  personRoleType: typeof SECTION_LEGAL_REPRESENTATIVES | typeof SECTION_ULTIMATE_BENEFICIAL_OWNERS
}

const DataSourceWizard: React.VFC<DataSourceWizardProps> = ({
  sourcesAreLoading,
  countryCode,
  initialize,
  legalRepresentativesOfSelectedSource,
  selectedDataSourceDetailsAreLoading,
  providerDetails,
  skip,
  ultimateBeneficialOwnersOfSelectedSource,
  submit,
  submitPending,
  hasError = false,
  initialFormData,
  personRoleType,
}) => {
  const {t} = useTranslation('charon')
  useDataPrefillLoader()

  useEffect(() => {
    initialize()
  }, [initialize])

  if (!countryCode || sourcesAreLoading) {
    return (
      <Row justifyContent="center" mt={100}>
        <Col col="auto">
          <InlineLoader />
        </Col>
      </Row>
    )
  }

  const hasSourceData =
    legalRepresentativesOfSelectedSource.length > 0 ||
    ultimateBeneficialOwnersOfSelectedSource.length > 0

  return (
    <>
      {selectedDataSourceDetailsAreLoading ? (
        <Row justifyContent="center" my={100}>
          <Col col="auto">
            <InlineLoader />
          </Col>
        </Row>
      ) : (
        <Form
          isLoading={submitPending}
          initialValues={initialFormData.initialValues}
          defaultValues={initialFormData.defaultValues}
          // in case when there are no data to be pre-filled / selected , submit button acts as skip
          onSubmit={hasSourceData ? submit : () => skip(personRoleType)}
        >
          <FormContentWrapper>
            {legalRepresentativesOfSelectedSource.length === 0 &&
              ultimateBeneficialOwnersOfSelectedSource.length === 0 && (
                <Row>
                  <Col>
                    <InfoAlert>{t('pre-fill-details-page.no-legal-entities-alert')}</InfoAlert>
                  </Col>
                </Row>
              )}
            {personRoleType === SECTION_LEGAL_REPRESENTATIVES &&
              legalRepresentativesOfSelectedSource.length > 0 && (
                <>
                  <Row>
                    <Col>
                      <H6>{t('pre-fill-details-page.legal-representatives.headline')}</H6>
                    </Col>
                  </Row>
                  <Row mb={40}>
                    <Col pt={20}>
                      <CheckBoxWrapper>
                        <InputWrapper
                          inputComponent={CheckboxGroupPureContainer}
                          name="legalRepresentatives"
                          options={mapPersonsToOptions(legalRepresentativesOfSelectedSource)}
                          t={t}
                        />
                      </CheckBoxWrapper>
                    </Col>
                  </Row>
                </>
              )}
            {personRoleType === SECTION_ULTIMATE_BENEFICIAL_OWNERS &&
              ultimateBeneficialOwnersOfSelectedSource.length > 0 && (
                <>
                  <Row mt={44}>
                    <Col>
                      <H6>{t('pre-fill-details-page.ultimate-beneficial-owners.headline')}</H6>
                    </Col>
                  </Row>
                  <Row mb={40}>
                    <Col pt={20}>
                      <CheckBoxWrapper>
                        <InputWrapper
                          inputComponent={CheckboxGroupPureContainer}
                          name="ultimateBeneficialOwners"
                          options={mapPersonsToOptions(ultimateBeneficialOwnersOfSelectedSource)}
                          t={t}
                        />
                      </CheckBoxWrapper>
                    </Col>
                  </Row>
                </>
              )}
            {providerDetails?.website && (
              <Row mb={50}>
                <Col>
                  <Small>
                    <Trans
                      i18nKey="pre-fill-details-page.provider-details.description"
                      components={[
                        <StyledLink to={providerDetails?.website} isExternal targetBlank />,
                      ]}
                      t={t}
                      values={{providerName: providerDetails?.name}}
                    />
                  </Small>
                </Col>
              </Row>
            )}
          </FormContentWrapper>
          <ConnectedFormFooterWrapper
            // @ts-expect-error need to fix
            loading={submitPending}
            hasError={!!hasError}
            hasNavLock={false}
            onDiscard={() => skip(personRoleType)}
            disableDiscard={selectedDataSourceDetailsAreLoading}
            isAlwaysVisible
            submitLabel={t('pre-fill-details-page.cta-submit')}
            discardLabel={t(`pre-fill-details-page.cta-skip`)}
            hasSourceData={hasSourceData}
          />
        </Form>
      )}
    </>
  )
}

export default DataSourceWizard
